"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const eventemitter3_1 = require("eventemitter3");
const util_1 = require("./util");
const logger_1 = require("./logger");
const socket_1 = require("./socket");
const mediaconnection_1 = require("./mediaconnection");
const dataconnection_1 = require("./dataconnection");
const enums_1 = require("./enums");
const api_1 = require("./api");
class PeerOptions {
}
/**
 * A peer who can initiate connections with other peers.
 */
class Peer extends eventemitter3_1.EventEmitter {
    constructor(id, options) {
        super();
        this._id = null;
        this._lastServerId = null;
        // States.
        this._destroyed = false; // Connections have been killed
        this._disconnected = false; // Connection to PeerServer killed but P2P connections still active
        this._open = false; // Sockets and such are not yet open.
        this._connections = new Map(); // All connections for this peer.
        this._lostMessages = new Map(); // src => [list of messages]
        let userId;
        // Deal with overloading
        if (id && id.constructor == Object) {
            options = id;
        }
        else if (id) {
            userId = id.toString();
        }
        // Configurize options
        options = Object.assign({ debug: 0, host: util_1.util.CLOUD_HOST, port: util_1.util.CLOUD_PORT, path: "/", key: Peer.DEFAULT_KEY, token: util_1.util.randomToken(), config: util_1.util.defaultConfig }, options);
        this._options = options;
        // Detect relative URL host.
        if (this._options.host === "/") {
            this._options.host = window.location.hostname;
        }
        // Set path correctly.
        if (this._options.path) {
            if (this._options.path[0] !== "/") {
                this._options.path = "/" + this._options.path;
            }
            if (this._options.path[this._options.path.length - 1] !== "/") {
                this._options.path += "/";
            }
        }
        // Set whether we use SSL to same as current host
        if (this._options.secure === undefined && this._options.host !== util_1.util.CLOUD_HOST) {
            this._options.secure = util_1.util.isSecure();
        }
        else if (this._options.host == util_1.util.CLOUD_HOST) {
            this._options.secure = true;
        }
        // Set a custom log function if present
        if (this._options.logFunction) {
            logger_1.default.setLogFunction(this._options.logFunction);
        }
        logger_1.default.logLevel = this._options.debug || 0;
        this._api = new api_1.API(options);
        this._socket = this._createServerConnection();
        // Sanity checks
        // Ensure WebRTC supported
        if (!util_1.util.supports.audioVideo && !util_1.util.supports.data) {
            this._delayedAbort(enums_1.PeerErrorType.BrowserIncompatible, "The current browser does not support WebRTC");
            return;
        }
        // Ensure alphanumeric id
        if (!!userId && !util_1.util.validateId(userId)) {
            this._delayedAbort(enums_1.PeerErrorType.InvalidID, `ID "${userId}" is invalid`);
            return;
        }
        if (userId) {
            this._initialize(userId);
        }
        else {
            this._api.retrieveId()
                .then(id => this._initialize(id))
                .catch(error => this._abort(enums_1.PeerErrorType.ServerError, error));
        }
    }
    get id() {
        return this._id;
    }
    get options() {
        return this._options;
    }
    get open() {
        return this._open;
    }
    get socket() {
        return this._socket;
    }
    /**
     * @deprecated
     * Return type will change from Object to Map<string,[]>
     */
    get connections() {
        const plainConnections = Object.create(null);
        for (let [k, v] of this._connections) {
            plainConnections[k] = v;
        }
        return plainConnections;
    }
    get destroyed() {
        return this._destroyed;
    }
    get disconnected() {
        return this._disconnected;
    }
    _createServerConnection() {
        const socket = new socket_1.Socket(this._options.secure, this._options.host, this._options.port, this._options.path, this._options.key, this._options.pingInterval);
        socket.on(enums_1.SocketEventType.Message, (data) => {
            this._handleMessage(data);
        });
        socket.on(enums_1.SocketEventType.Error, (error) => {
            this._abort(enums_1.PeerErrorType.SocketError, error);
        });
        socket.on(enums_1.SocketEventType.Disconnected, () => {
            if (this.disconnected) {
                return;
            }
            this.emitError(enums_1.PeerErrorType.Network, "Lost connection to server.");
            this.disconnect();
        });
        socket.on(enums_1.SocketEventType.Close, () => {
            if (this.disconnected) {
                return;
            }
            this._abort(enums_1.PeerErrorType.SocketClosed, "Underlying socket is already closed.");
        });
        return socket;
    }
    /** Initialize a connection with the server. */
    _initialize(id) {
        this._id = id;
        this.socket.start(id, this._options.token);
    }
    /** Handles messages from the server. */
    _handleMessage(message) {
        const type = message.type;
        const payload = message.payload;
        const peerId = message.src;
        switch (type) {
            case enums_1.ServerMessageType.Open: // The connection to the server is open.
                this._lastServerId = this.id;
                this._open = true;
                this.emit(enums_1.PeerEventType.Open, this.id);
                break;
            case enums_1.ServerMessageType.Error: // Server error.
                this._abort(enums_1.PeerErrorType.ServerError, payload.msg);
                break;
            case enums_1.ServerMessageType.IdTaken: // The selected ID is taken.
                this._abort(enums_1.PeerErrorType.UnavailableID, `ID "${this.id}" is taken`);
                break;
            case enums_1.ServerMessageType.InvalidKey: // The given API key cannot be found.
                this._abort(enums_1.PeerErrorType.InvalidKey, `API KEY "${this._options.key}" is invalid`);
                break;
            case enums_1.ServerMessageType.Leave: // Another peer has closed its connection to this peer.
                logger_1.default.log(`Received leave message from ${peerId}`);
                this._cleanupPeer(peerId);
                this._connections.delete(peerId);
                break;
            case enums_1.ServerMessageType.Expire: // The offer sent to a peer has expired without response.
                this.emitError(enums_1.PeerErrorType.PeerUnavailable, `Could not connect to peer ${peerId}`);
                break;
            case enums_1.ServerMessageType.Offer: {
                // we should consider switching this to CALL/CONNECT, but this is the least breaking option.
                const connectionId = payload.connectionId;
                let connection = this.getConnection(peerId, connectionId);
                if (connection) {
                    connection.close();
                    logger_1.default.warn(`Offer received for existing Connection ID:${connectionId}`);
                }
                // Create a new connection.
                if (payload.type === enums_1.ConnectionType.Media) {
                    connection = new mediaconnection_1.MediaConnection(peerId, this, {
                        connectionId: connectionId,
                        _payload: payload,
                        metadata: payload.metadata,
                        config: payload.config
                    });
                    this._addConnection(peerId, connection);
                    this.emit(enums_1.PeerEventType.Call, connection);
                }
                else if (payload.type === enums_1.ConnectionType.Data) {
                    connection = new dataconnection_1.DataConnection(peerId, this, {
                        connectionId: connectionId,
                        _payload: payload,
                        metadata: payload.metadata,
                        label: payload.label,
                        serialization: payload.serialization,
                        reliable: payload.reliable,
                        config: payload.config
                    });
                    this._addConnection(peerId, connection);
                    this.emit(enums_1.PeerEventType.Connection, connection);
                }
                else {
                    logger_1.default.warn(`Received malformed connection type:${payload.type}`);
                    return;
                }
                // Find messages.
                const messages = this._getMessages(connectionId);
                for (let message of messages) {
                    connection.handleMessage(message);
                }
                break;
            }
            default: {
                if (!payload) {
                    logger_1.default.warn(`You received a malformed message from ${peerId} of type ${type}`);
                    return;
                }
                const connectionId = payload.connectionId;
                const connection = this.getConnection(peerId, connectionId);
                if (connection && connection.peerConnection) {
                    // Pass it on.
                    connection.handleMessage(message);
                }
                else if (connectionId) {
                    // Store for possible later use
                    this._storeMessage(connectionId, message);
                }
                else {
                    logger_1.default.warn("You received an unrecognized message:", message);
                }
                break;
            }
        }
    }
    /** Stores messages without a set up connection, to be claimed later. */
    _storeMessage(connectionId, message) {
        if (!this._lostMessages.has(connectionId)) {
            this._lostMessages.set(connectionId, []);
        }
        this._lostMessages.get(connectionId).push(message);
    }
    /** Retrieve messages from lost message store */
    //TODO Change it to private
    _getMessages(connectionId) {
        const messages = this._lostMessages.get(connectionId);
        if (messages) {
            this._lostMessages.delete(connectionId);
            return messages;
        }
        return [];
    }
    /**
     * Returns a DataConnection to the specified peer. See documentation for a
     * complete list of options.
     */
    connect(peer, options = {}) {
        if (this.disconnected) {
            logger_1.default.warn("You cannot connect to a new Peer because you called " +
                ".disconnect() on this Peer and ended your connection with the " +
                "server. You can create a new Peer to reconnect, or call reconnect " +
                "on this peer if you believe its ID to still be available.");
            this.emitError(enums_1.PeerErrorType.Disconnected, "Cannot connect to new Peer after disconnecting from server.");
            return;
        }
        const dataConnection = new dataconnection_1.DataConnection(peer, this, options);
        this._addConnection(peer, dataConnection);
        return dataConnection;
    }
    /**
     * Returns a MediaConnection to the specified peer. See documentation for a
     * complete list of options.
     */
    call(peer, stream, options = {}) {
        if (this.disconnected) {
            logger_1.default.warn("You cannot connect to a new Peer because you called " +
                ".disconnect() on this Peer and ended your connection with the " +
                "server. You can create a new Peer to reconnect.");
            this.emitError(enums_1.PeerErrorType.Disconnected, "Cannot connect to new Peer after disconnecting from server.");
            return;
        }
        if (!stream) {
            logger_1.default.error("To call a peer, you must provide a stream from your browser's `getUserMedia`.");
            return;
        }
        options._stream = stream;
        const mediaConnection = new mediaconnection_1.MediaConnection(peer, this, options);
        this._addConnection(peer, mediaConnection);
        return mediaConnection;
    }
    /** Add a data/media connection to this peer. */
    _addConnection(peerId, connection) {
        logger_1.default.log(`add connection ${connection.type}:${connection.connectionId} to peerId:${peerId}`);
        if (!this._connections.has(peerId)) {
            this._connections.set(peerId, []);
        }
        this._connections.get(peerId).push(connection);
    }
    //TODO should be private
    _removeConnection(connection) {
        const connections = this._connections.get(connection.peer);
        if (connections) {
            const index = connections.indexOf(connection);
            if (index !== -1) {
                connections.splice(index, 1);
            }
        }
        //remove from lost messages
        this._lostMessages.delete(connection.connectionId);
    }
    /** Retrieve a data/media connection for this peer. */
    getConnection(peerId, connectionId) {
        const connections = this._connections.get(peerId);
        if (!connections) {
            return null;
        }
        for (let connection of connections) {
            if (connection.connectionId === connectionId) {
                return connection;
            }
        }
        return null;
    }
    _delayedAbort(type, message) {
        setTimeout(() => {
            this._abort(type, message);
        }, 0);
    }
    /**
     * Emits an error message and destroys the Peer.
     * The Peer is not destroyed if it's in a disconnected state, in which case
     * it retains its disconnected state and its existing connections.
     */
    _abort(type, message) {
        logger_1.default.error("Aborting!");
        this.emitError(type, message);
        if (!this._lastServerId) {
            this.destroy();
        }
        else {
            this.disconnect();
        }
    }
    /** Emits a typed error message. */
    emitError(type, err) {
        logger_1.default.error("Error:", err);
        let error;
        if (typeof err === "string") {
            error = new Error(err);
        }
        else {
            error = err;
        }
        error.type = type;
        this.emit(enums_1.PeerEventType.Error, error);
    }
    /**
     * Destroys the Peer: closes all active connections as well as the connection
     *  to the server.
     * Warning: The peer can no longer create or accept connections after being
     *  destroyed.
     */
    destroy() {
        if (this.destroyed) {
            return;
        }
        logger_1.default.log(`Destroy peer with ID:${this.id}`);
        this.disconnect();
        this._cleanup();
        this._destroyed = true;
        this.emit(enums_1.PeerEventType.Close);
    }
    /** Disconnects every connection on this peer. */
    _cleanup() {
        for (let peerId of this._connections.keys()) {
            this._cleanupPeer(peerId);
            this._connections.delete(peerId);
        }
        this.socket.removeAllListeners();
    }
    /** Closes all connections to this peer. */
    _cleanupPeer(peerId) {
        const connections = this._connections.get(peerId);
        if (!connections)
            return;
        for (let connection of connections) {
            connection.close();
        }
    }
    /**
     * Disconnects the Peer's connection to the PeerServer. Does not close any
     *  active connections.
     * Warning: The peer can no longer create or accept connections after being
     *  disconnected. It also cannot reconnect to the server.
     */
    disconnect() {
        if (this.disconnected) {
            return;
        }
        const currentId = this.id;
        logger_1.default.log(`Disconnect peer with ID:${currentId}`);
        this._disconnected = true;
        this._open = false;
        this.socket.close();
        this._lastServerId = currentId;
        this._id = null;
        this.emit(enums_1.PeerEventType.Disconnected, currentId);
    }
    /** Attempts to reconnect with the same ID. */
    reconnect() {
        if (this.disconnected && !this.destroyed) {
            logger_1.default.log(`Attempting reconnection to server with ID ${this._lastServerId}`);
            this._disconnected = false;
            this._initialize(this._lastServerId);
        }
        else if (this.destroyed) {
            throw new Error("This peer cannot reconnect to the server. It has already been destroyed.");
        }
        else if (!this.disconnected && !this.open) {
            // Do nothing. We're still connecting the first time.
            logger_1.default.error("In a hurry? We're still trying to make the initial connection!");
        }
        else {
            throw new Error(`Peer ${this.id} cannot reconnect because it is not disconnected from the server!`);
        }
    }
    /**
     * Get a list of available peer IDs. If you're running your own server, you'll
     * want to set allow_discovery: true in the PeerServer options. If you're using
     * the cloud server, email team@peerjs.com to get the functionality enabled for
     * your key.
     */
    listAllPeers(cb = (_) => { }) {
        this._api.listAllPeers()
            .then(peers => cb(peers))
            .catch(error => this._abort(enums_1.PeerErrorType.ServerError, error));
    }
}
exports.Peer = Peer;
Peer.DEFAULT_KEY = "peerjs";
